import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Alert,
} from 'reactstrap';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';
import alumnosApiService from '../../services/http/alumnosApiService';
import logger from '../../services/logger';

// Modal para solicitar pendientes
const SolicitarPendientes = ({ isOpen, toggle, pendientes, onSuccess }) => {
  const [seleccionados, setSeleccionados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const { empresaId, studentId } = useParams();

  const toggleCheck = (id) => {
    setSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const handleSolicitar = async () => {
    setLoading(true);
    setStatus(null);

    try {
      const documentosSeleccionados = pendientes
        .filter((req) => seleccionados.includes(req.id))
        .map((req) => req.nombre);

      const payload = {
        ccodcli: studentId,
        headquarter: empresaId,
        documentos: documentosSeleccionados,
      };

      await alumnosApiService.enviarMailRequisitos(payload);

      setStatus('success');
      setSeleccionados([]);
      if (onSuccess) onSuccess();
    } catch (error) {
      logger.error('Error al solicitar requisitos:', error);
      setStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Solicitar pendientes</ModalHeader>
      <ModalBody>
        {pendientes.length === 0 ? (
          <p>No hay requisitos pendientes.</p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '10px',
            }}
          >
            {pendientes.map((req) => (
              <FormGroup check key={req.id}>
                <Label check>
                  <Input
                    type="checkbox"
                    checked={seleccionados.includes(req.id)}
                    onChange={() => toggleCheck(req.id)}
                  />{' '}
                  {req.nombre}
                </Label>
              </FormGroup>
            ))}
          </div>
        )}

        {status === 'success' && (
          <Alert color="success" className="mt-3">
            Correo enviado con éxito.
          </Alert>
        )}
        {status === 'error' && (
          <Alert color="danger" className="mt-3">
            Error al enviar el correo. Intenta nuevamente.
          </Alert>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={loading}>
          Cerrar
        </Button>
        <Button
          color="primary"
          onClick={handleSolicitar}
          disabled={seleccionados.length === 0 || loading}
        >
          {loading ? 'Enviando...' : 'Solicitar'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SolicitarPendientes.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  pendientes: PropTypes.array.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

// Componente principal Requerimientos
const Requerimientos = () => {
  const [requerimientos, setRequerimientos] = useState([]);
  const [modalPendientes, setModalPendientes] = useState(false);
  const [modalResultado, setModalResultado] = useState(false);
  const [resultado, setResultado] = useState(null); // 'success' | 'error' | null
  const { empresaId, studentId } = useParams();

  const fetchData = useCallback(async () => {
    try {
      const dataActas = await alumnosApiService.obtenerRequisitosAlumno({
        headquarter: empresaId,
        ccodcli: studentId,
      });
      setRequerimientos(dataActas);
    } catch (error) {
      setRequerimientos([]);
    }
  }, [empresaId, studentId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const toggleCheck = (id) => {
    setRequerimientos((prev) =>
      prev.map((req) => (req.id === id ? { ...req, checked: !req.checked } : req)),
    );
  };

  const handleSave = async () => {
    const payload = requerimientos.map((req) => ({
      headquarter: empresaId,
      ccodcli: req.ccodcli,
      doc_id: req.id,
      estado: req.checked ? 1 : null,
    }));

    try {
      await alumnosApiService.actualizarRequisitos({ payload });
      setResultado('success');
      fetchData(); // Refrescar datos después de guardar
    } catch (error) {
      logger.error('Error al actualizar requisitos:', error);
      setResultado('error');
    } finally {
      setModalResultado(true);
    }
  };

  const togglePendientes = () => setModalPendientes(!modalPendientes);
  const closeResultadoModal = () => setModalResultado(false);

  const pendientes = requerimientos.filter((req) => !req.checked);

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    const dia = String(date.getDate()).padStart(2, '0');
    const mes = String(date.getMonth() + 1).padStart(2, '0');
    const año = date.getFullYear();
    const horas = String(date.getHours()).padStart(2, '0');
    const minutos = String(date.getMinutes()).padStart(2, '0');
    const segundos = String(date.getSeconds()).padStart(2, '0');
    return `${dia}-${mes}-${año} ${horas}:${minutos}:${segundos}`;
  };

  return (
    <div className="p-3">
      <div
        className="requerimientos-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
          gap: '10px',
        }}
      >
        {requerimientos.map((req) => (
          <FormGroup check key={req.id}>
            <Label check>
              <Input type="checkbox" checked={req.checked} onChange={() => toggleCheck(req.id)} />{' '}
              {req.nombre}{' '}
              {req.estado === 0 || req.estado === '0'
                ? `  - (Solicitado ${formatFecha(req.fechaSol)})`
                : ''}
            </Label>
          </FormGroup>
        ))}
      </div>

      <div className="d-flex gap-2 mt-3">
        <Button color="primary" onClick={handleSave}>
          <Icon.Save className="me-1" /> Guardar
        </Button>
        <Button color="warning" onClick={togglePendientes}>
          Solicitar pendientes
        </Button>
      </div>

      <SolicitarPendientes
        isOpen={modalPendientes}
        toggle={togglePendientes}
        pendientes={pendientes}
        onSuccess={fetchData}
      />

      {/* Modal de resultado del guardado */}
      <Modal isOpen={modalResultado} toggle={closeResultadoModal}>
        <ModalHeader toggle={closeResultadoModal}>
          {resultado === 'success' ? 'Guardado exitoso' : 'Error al guardar'}
        </ModalHeader>
        <ModalBody>
          {resultado === 'success' ? (
            <Alert color="success" className="mb-0">
              Los requisitos se guardaron correctamente.
            </Alert>
          ) : (
            <Alert color="danger" className="mb-0">
              Hubo un error al guardar los requisitos. Inténtalo nuevamente.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={closeResultadoModal}>
            Aceptar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Requerimientos;
